import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Link,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  People as PeopleIcon,
  SmartToy as AIIcon,
  IntegrationInstructions as IntegrationIcon,
  Tune as TuneIcon,
  ViewModule as ModuleIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckIcon,
  Block as BlockIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  MarkEmailRead as MarkReadIcon,
  Language as WidgetIcon,
  Instagram as InstagramIcon,
  Chat as ChatwootIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  // New state for testing user_files RLS
  const [userFiles, setUserFiles] = useState([]);
  const [loadingUserFiles, setLoadingUserFiles] = useState(false);
  const [userFilesError, setUserFilesError] = useState(null);
  const [userFilesMessage, setUserFilesMessage] = useState('');

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // User integrations state
  const [userIntegrations, setUserIntegrations] = useState({});

  const navigate = useNavigate();

  // Helper to get JWT token for fetch requests
  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      // Get unread count
      const { data: countData, error: countError } = await supabase.rpc('get_unread_notification_count');
      if (countError) {
        console.error('Error fetching unread count:', countError);
      } else {
        setUnreadCount(countData || 0);
      }

      // Get recent notifications
      const { data: notifData, error: notifError } = await supabase.rpc('get_admin_notifications', {
        p_limit: 10,
        p_offset: 0
      });
      if (notifError) {
        console.error('Error fetching notifications:', notifError);
      } else {
        setNotifications(notifData || []);
      }
    } catch (err) {
      console.error('Error in fetchNotifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  // Fetch user integrations (widget, instagram, chatwoot)
  const fetchUserIntegrations = useCallback(async () => {
    try {
      // Fetch widget configurations with last session activity
      const { data: widgets, error: widgetError } = await supabase
        .from('widget_configurations')
        .select('rag_user_id, allowed_domains, is_active, widget_name');

      if (widgetError) {
        console.error('Error fetching widgets:', widgetError);
      }

      // Fetch latest session per user to determine if widget is actively installed
      const { data: sessions, error: sessionError } = await supabase
        .from('widget_sessions')
        .select('rag_user_id, created_at')
        .order('created_at', { ascending: false });

      if (sessionError) {
        console.error('Error fetching sessions:', sessionError);
      }

      // Fetch instagram channels
      const { data: instagrams, error: instaError } = await supabase
        .from('instagram_channels')
        .select('owner_id, instagram_username');

      if (instaError) {
        console.error('Error fetching instagram:', instaError);
      }

      // Fetch chatwoot configurations  
      const { data: chatwoots, error: chatwootError } = await supabase
        .from('chatwoot_configurations')
        .select('owner_id, inbox_name');

      if (chatwootError) {
        console.error('Error fetching chatwoot:', chatwootError);
      }

      // Build integrations map per user
      const integrations = {};
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

      // Build latest session map
      const latestSessionByUser = {};
      (sessions || []).forEach(s => {
        if (!latestSessionByUser[s.rag_user_id]) {
          latestSessionByUser[s.rag_user_id] = new Date(s.created_at);
        }
      });

      // Process widgets - collect all domains from all widgets per user and deduplicate
      (widgets || []).forEach(w => {
        if (!integrations[w.rag_user_id]) {
          integrations[w.rag_user_id] = {};
        }
        const lastSession = latestSessionByUser[w.rag_user_id];

        // Merge domains from multiple widgets for the same user
        const existingDomains = integrations[w.rag_user_id].widget?.domains || [];
        const newDomains = w.allowed_domains || [];
        const allDomains = [...new Set([...existingDomains, ...newDomains])]; // Deduplicate

        integrations[w.rag_user_id].widget = {
          configured: true,
          domains: allDomains,
          name: w.widget_name,
          isActive: w.is_active || integrations[w.rag_user_id].widget?.isActive,
          hasRecentSession: (lastSession && lastSession > oneDayAgo) || integrations[w.rag_user_id].widget?.hasRecentSession
        };
      });

      // Process instagram
      (instagrams || []).forEach(i => {
        if (!integrations[i.owner_id]) {
          integrations[i.owner_id] = {};
        }
        integrations[i.owner_id].instagram = {
          configured: true,
          username: i.instagram_username
        };
      });

      // Process chatwoot
      (chatwoots || []).forEach(c => {
        if (!integrations[c.owner_id]) {
          integrations[c.owner_id] = {};
        }
        integrations[c.owner_id].chatwoot = {
          configured: true,
          inboxName: c.inbox_name
        };
      });

      setUserIntegrations(integrations);
    } catch (err) {
      console.error('Error fetching user integrations:', err);
    }
  }, []);

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const { error } = await supabase.rpc('mark_notification_read', { p_notification_id: notificationId });
      if (error) throw error;
      // Refresh notifications
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      const { error } = await supabase.rpc('mark_all_notifications_read');
      if (error) throw error;
      // Refresh notifications
      fetchNotifications();
    } catch (err) {
      console.error('Error marking all notifications read:', err);
    }
  };

  // Get severity icon
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon sx={{ color: '#d32f2f' }} />;
      case 'high':
        return <WarningIcon sx={{ color: '#ed6c02' }} />;
      case 'medium':
        return <InfoIcon sx={{ color: '#0288d1' }} />;
      default:
        return <InfoIcon sx={{ color: '#2e7d32' }} />;
    }
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return { bg: '#ffebee', text: '#d32f2f' };
      case 'high': return { bg: '#fff3e0', text: '#ed6c02' };
      case 'medium': return { bg: '#e3f2fd', text: '#0288d1' };
      default: return { bg: '#e8f5e9', text: '#2e7d32' };
    }
  };

  // Notification popover handlers
  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const notificationPopoverOpen = Boolean(notificationAnchorEl);

  useEffect(() => {
    const checkAdminStatusAndLoadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;

        if (!session) {
          setError('No user logged in. Please log in as an admin.');
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        // Fetch the user object to get raw_app_meta_data
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;

        if (!user) {
          setError('Could not retrieve user details.');
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        // REVERTING to check app_metadata first, then raw_app_meta_data, as JWT shows role in app_metadata
        const isUserTrulyAdmin = user?.app_metadata?.app_role === 'admin' || user?.raw_app_meta_data?.app_role === 'admin';
        setIsAdmin(isUserTrulyAdmin);

        if (isUserTrulyAdmin) {
          // Fetch notifications
          fetchNotifications();

          // Fetch user integrations (widget domains, instagram, chatwoot)
          fetchUserIntegrations();

          // Fetch users via Edge Function
          const token = await getAuthToken();
          if (!token) {
            setError("Could not retrieve auth token for admin operations.");
            setUsers([]);
            setLoading(false);
            return;
          }
          try {
            if (!API_BASE_URL) {
              throw new Error("API base URL is not configured.");
            }
            const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
            if (!anonKey) {
              throw new Error("Supabase anon key is not configured in the frontend environment.");
            }
            const response = await fetch(`${API_BASE_URL}/functions/v1/admin-user-manager/users`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'apikey': anonKey,
              }
            });
            const data = await response.json();
            if (!response.ok) {
              throw new Error(data.error || `Failed to list users: ${response.status}`);
            }
            setUsers(data.users || []);
          } catch (listUsersErr) {
            console.error('Error listing users via Edge Function:', listUsersErr);
            setError(`Could not list users: ${listUsersErr.message}`);
            setUsers([]);
          }
        } else {
          setError('You do not have permission to access the admin panel.');
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError(err.message || 'An unexpected error occurred.');
        setIsAdmin(false); // Ensure isAdmin is false on error
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatusAndLoadData();
  }, [getAuthToken, fetchNotifications, fetchUserIntegrations]);

  const handleRoleToggle = async (userToUpdate) => {
    setError(null);
    const originalUsers = [...users]; // Keep a copy for optimistic update rollback

    // Optimistic UI update
    setUsers(prevUsers =>
      prevUsers.map(u =>
        u.id === userToUpdate.id
          ? { ...u, user_metadata: { ...u.user_metadata, role: u.user_metadata?.role === 'admin' ? 'user' : 'admin' } }
          : u
      )
    );

    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Auth token not available.");

      const currentRole = userToUpdate.user_metadata?.role === 'admin' ? 'admin' : 'user';
      const newRole = currentRole === 'admin' ? 'user' : 'admin';

      if (!API_BASE_URL) {
        throw new Error("API base URL is not configured.");
      }
      const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!anonKey) {
        throw new Error("Supabase anon key is not configured in the frontend environment.");
      }
      const response = await fetch(`${API_BASE_URL}/functions/v1/admin-user-manager/users/${userToUpdate.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': anonKey,
        },
        body: JSON.stringify({ user_metadata: { ...userToUpdate.user_metadata, role: newRole } })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Failed to toggle role: ${response.status}`);
      }

      // Confirm update by re-fetching (or just update local state if response is reliable)
      // For simplicity, we'll update based on the optimistic update having worked, 
      // but a re-fetch or using the response `data` is more robust.
      // The optimistic update already handled the visual change.
      console.log("Role toggled successfully for user:", data); // `data` should be the updated user from the Edge Function

    } catch (err) {
      console.error('Error toggling role via Edge Function:', err);
      setError(`Failed to toggle role: ${err.message}`);
      setUsers(originalUsers); // Rollback optimistic update on error
    }
  };

  const fetchUserFilesForAdmin = async () => {
    if (!isAdmin) {
      setUserFilesError("You are not authorized to perform this action.");
      return;
    }
    setLoadingUserFiles(true);
    setUserFilesError(null);
    setUserFilesMessage('');
    try {
      const { data, error } = await supabase.from('user_files').select('*');
      if (error) throw error;
      setUserFiles(data || []);
      setUserFilesMessage(`Successfully fetched ${data?.length || 0} file records.`);
    } catch (err) {
      console.error("Error fetching user files:", err);
      setUserFilesError(err.message);
      setUserFiles([]);
    } finally {
      setLoadingUserFiles(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 2 }}>
          {error || 'You do not have permission to access the admin panel.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        pt: 4,
        pb: 8,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 6, position: 'relative' }}>
          {/* Notification Bell - Top Right */}
          <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
            <Tooltip title="Notifications">
              <IconButton
                onClick={handleNotificationClick}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                  width: 48,
                  height: 48,
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' },
                        '100%': { transform: 'scale(1)' },
                      },
                    },
                  }}
                >
                  {unreadCount > 0 ? <NotificationsActiveIcon /> : <NotificationsIcon />}
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>

          {/* Notification Popover */}
          <Popover
            open={notificationPopoverOpen}
            anchorEl={notificationAnchorEl}
            onClose={handleNotificationClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                width: 380,
                maxHeight: 480,
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              },
            }}
          >
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Notifications
                {unreadCount > 0 && (
                  <Chip
                    label={unreadCount}
                    size="small"
                    color="error"
                    sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                  />
                )}
              </Typography>
              {unreadCount > 0 && (
                <Tooltip title="Mark all as read">
                  <IconButton size="small" onClick={markAllNotificationsRead}>
                    <MarkReadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {loadingNotifications ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', color: '#888' }}>
                <NotificationsIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                <Typography>No notifications</Typography>
              </Box>
            ) : (
              <List sx={{ p: 0, maxHeight: 360, overflow: 'auto' }}>
                {notifications.map((notif) => {
                  const colors = getSeverityColor(notif.severity);
                  return (
                    <ListItem
                      key={notif.id}
                      onClick={() => markNotificationRead(notif.id)}
                      sx={{
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        bgcolor: notif.status === 'unread' ? colors.bg : 'transparent',
                        '&:hover': { bgcolor: '#f5f5f5' },
                        py: 1.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getSeverityIcon(notif.severity)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: notif.status === 'unread' ? 600 : 400 }}>
                            {notif.title}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', mb: 0.5 }}>
                              {notif.message.length > 80 ? `${notif.message.substring(0, 80)}...` : notif.message}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={notif.notification_type.replace('_', ' ')}
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                              />
                              <Typography variant="caption" sx={{ color: '#999' }}>
                                {new Date(notif.created_at).toLocaleString()}
                              </Typography>
                            </Stack>
                          </>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}

            <Box sx={{ p: 1.5, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
              <Button
                component={RouterLink}
                to="/admin/notifications"
                size="small"
                sx={{ textTransform: 'none', fontWeight: 500 }}
                onClick={handleNotificationClose}
              >
                View All Notifications
              </Button>
            </Box>
          </Popover>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: 'white',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                letterSpacing: '-0.02em',
              }}
            >
              Admin Control Center
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 400,
                fontSize: { xs: '1rem', md: '1.25rem' },
              }}
            >
              Manage your platform with ease
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Management Cards Section */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'white',
              mb: 3,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
            }}
          >
            Management Sections
          </Typography>
          <Grid container spacing={3}>
            {/* Subscription Packages Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to="/admin/packages"
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textDecoration: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Subscription Packages
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Configure pricing tiers and subscription plans
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Live Human Agent Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to="/admin/live-agent"
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textDecoration: 'none',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Live Human Agent
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Manage live chat agents and availability
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* LLM Models Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to="/admin/models"
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textDecoration: 'none',
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    <AIIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    LLM Models
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Configure AI language models and settings
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Embedding Models Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to="/admin/embedding-models"
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textDecoration: 'none',
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    <ModuleIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Embedding Models
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Manage vector embedding configurations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* System Defaults Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to="/admin/system-defaults"
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textDecoration: 'none',
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    <TuneIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    System Defaults
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Configure system-wide default settings
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Marketplace Integrations Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to="/admin/integrations"
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textDecoration: 'none',
                  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                  color: '#1a1a1a',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.1)',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    <IntegrationIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Marketplace Integrations
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Connect with external platforms and services
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Demo Management Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to="/demo-admin"
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textDecoration: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    <DashboardIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Demo Management
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Create and manage customer demo pages
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* System Health Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to="/admin/system-health"
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textDecoration: 'none',
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    <SecurityIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    System Health
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Monitor crawl status and fix embedding issues
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Notifications Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to="/admin/notifications"
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textDecoration: 'none',
                  background: unreadCount > 0
                    ? 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)'
                    : 'linear-gradient(135deg, #6a85b6 0%, #bac8e0 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  },
                  position: 'relative',
                }}
              >
                {unreadCount > 0 && (
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    max={99}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      '& .MuiBadge-badge': {
                        fontSize: '0.9rem',
                        height: 24,
                        minWidth: 24,
                        borderRadius: 12,
                      },
                    }}
                  >
                    <Box />
                  </Badge>
                )}
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    {unreadCount > 0 ? <NotificationsActiveIcon sx={{ fontSize: 32 }} /> : <NotificationsIcon sx={{ fontSize: 32 }} />}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Notifications
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {unreadCount > 0
                      ? `${unreadCount} unread alert${unreadCount !== 1 ? 's' : ''} requiring attention`
                      : 'View system alerts and notifications'
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* User Management Section */}
        <Box sx={{ mb: 6 }}>
          <Paper
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                px: 4,
                py: 3,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                  <AdminIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                    User Management
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Manage user roles and permissions
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <Box sx={{ p: 4 }}>
              <Alert
                severity="info"
                icon={<SecurityIcon />}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: '#e3f2fd',
                  '& .MuiAlert-icon': {
                    color: '#1976d2',
                  },
                }}
              >
                Role changes update user_metadata.role. Admin panel access is controlled by
                raw_app_meta_data.app_role.
              </Alert>
              <TableContainer>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#666' }}>
                        Email Address
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#666' }}>
                        Domains
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#666' }}>
                        Integrations
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#666' }}>
                        Current Role
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#666' }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow
                        key={user.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                          transition: 'background-color 0.2s',
                        }}
                      >
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={`/admin/user/${user.id}`}
                            underline="hover"
                            sx={{
                              fontWeight: 500,
                              color: '#667eea',
                              '&:hover': {
                                color: '#764ba2',
                              },
                            }}
                          >
                            {user.email}
                          </Link>
                        </TableCell>
                        {/* Domains Column */}
                        <TableCell>
                          {userIntegrations[user.id]?.widget?.domains?.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {userIntegrations[user.id].widget.domains.map((domain, idx) => (
                                <Link
                                  key={idx}
                                  href={domain.startsWith('http') ? domain : `https://${domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  underline="hover"
                                  sx={{
                                    fontSize: '0.85rem',
                                    color: '#0288d1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': { color: '#01579b' }
                                  }}
                                >
                                  {domain.replace(/^https?:\/\//, '')}
                                  <OpenInNewIcon sx={{ fontSize: 12 }} />
                                </Link>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        {/* Integrations Column */}
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            {/* Widget Icon */}
                            {userIntegrations[user.id]?.widget?.configured && (
                              <Tooltip
                                title={
                                  userIntegrations[user.id].widget.hasRecentSession
                                    ? `Widget: ${userIntegrations[user.id].widget.name || 'Active'} (Script detected)`
                                    : `Widget: ${userIntegrations[user.id].widget.name || 'Configured'} (No recent activity)`
                                }
                              >
                                <WidgetIcon
                                  sx={{
                                    color: userIntegrations[user.id].widget.hasRecentSession ? '#4caf50' : '#9e9e9e',
                                    fontSize: 24
                                  }}
                                />
                              </Tooltip>
                            )}
                            {/* Instagram Icon */}
                            {userIntegrations[user.id]?.instagram?.configured && (
                              <Tooltip title={`Instagram: @${userIntegrations[user.id].instagram.username || 'connected'}`}>
                                <InstagramIcon sx={{ color: '#E4405F', fontSize: 24 }} />
                              </Tooltip>
                            )}
                            {/* Chatwoot Icon */}
                            {userIntegrations[user.id]?.chatwoot?.configured && (
                              <Tooltip title={`Chatwoot: ${userIntegrations[user.id].chatwoot.inboxName || 'connected'}`}>
                                <ChatwootIcon sx={{ color: '#1F93FF', fontSize: 24 }} />
                              </Tooltip>
                            )}
                            {/* No integrations */}
                            {!userIntegrations[user.id]?.widget?.configured &&
                              !userIntegrations[user.id]?.instagram?.configured &&
                              !userIntegrations[user.id]?.chatwoot?.configured && (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.user_metadata?.role || 'user'}
                            size="small"
                            icon={
                              user.user_metadata?.role === 'admin' ? (
                                <CheckIcon />
                              ) : (
                                <BlockIcon />
                              )
                            }
                            sx={{
                              backgroundColor:
                                user.user_metadata?.role === 'admin'
                                  ? '#e8f5e9'
                                  : '#f3e5f5',
                              color:
                                user.user_metadata?.role === 'admin' ? '#2e7d32' : '#7b1fa2',
                              fontWeight: 600,
                              borderRadius: 2,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleRoleToggle(user)}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 3,
                              backgroundColor:
                                user.user_metadata?.role === 'admin' ? '#f5576c' : '#667eea',
                              '&:hover': {
                                backgroundColor:
                                  user.user_metadata?.role === 'admin' ? '#e0445a' : '#5568d3',
                              },
                            }}
                          >
                            {user.user_metadata?.role === 'admin'
                              ? 'Remove Admin'
                              : 'Make Admin'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>

        {/* RLS Test Section */}
        <Paper
          sx={{
            borderRadius: 4,
            p: 4,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#333' }}>
            Database Security Test
          </Typography>
          <Button
            variant="contained"
            onClick={fetchUserFilesForAdmin}
            disabled={loadingUserFiles || !isAdmin}
            startIcon={loadingUserFiles ? <CircularProgress size={20} /> : <SecurityIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
              },
            }}
          >
            {loadingUserFiles ? 'Loading...' : 'Fetch All User Files (RLS Test)'}
          </Button>
          {userFilesError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {`Error fetching files: ${userFilesError}`}
            </Alert>
          )}
          {userFilesMessage && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
              {userFilesMessage}
            </Alert>
          )}
          {userFiles.length > 0 && (
            <>
              <Typography sx={{ mt: 3, mb: 2, fontWeight: 600, color: '#666' }}>
                First 5 file records (raw data):
              </Typography>
              {userFiles.slice(0, 5).map((file) => (
                <Paper
                  key={file.id}
                  sx={{
                    p: 2,
                    my: 1,
                    fontSize: '0.8rem',
                    overflowX: 'auto',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <pre style={{ margin: 0 }}>{JSON.stringify(file, null, 2)}</pre>
                </Paper>
              ))}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminPanel;
